import styled from 'styled-components'

export const fleetColors = {
  success: '#22C55E',
  warning: '#F97316',
  yellow: '#EAB308',
  danger: '#EF4444',
  info: '#0EA5E9',
  purple: '#A855F7',
  trackBg: '#3A3632',
  cardSurface: 'rgba(34, 32, 32, 0.55)',
  cardInner: 'rgba(20, 19, 19, 0.55)',
  divider: 'rgba(255, 255, 255, 0.08)',
}

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

export const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`

export const KpiCard = styled.div<{ accent?: string }>`
  background: ${({ theme }) => theme.colorSurface1};
  border: 1px solid ${({ theme }) => theme.colorDividerDefault};
  border-left: 3px solid ${({ accent, theme }) => accent || theme.colorOrange};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-height: 110px;
  backdrop-filter: blur(8px);
`

export const KpiValue = styled.div<{ accent?: string; size?: number }>`
  font-size: ${(p) => p.size || 32}px;
  font-weight: 800;
  line-height: 1;
  color: ${({ accent, theme }) => accent || theme.textColor};
`

export const DashGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`

export const Panel = styled.div`
  background: ${({ theme }) => theme.colorSurface1};
  border: 1px solid ${({ theme }) => theme.colorDividerDefault};
  border-radius: 12px;
  padding: 1.25rem;
  backdrop-filter: blur(8px);
`

export const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;

  .view-all {
    color: ${({ theme }) => theme.colorOrange};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      filter: brightness(115%);
    }
  }
`

export const CompositionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`

export const CompositionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: ${({ theme }) => theme.colorSurfaceBase};
  border: 1px solid ${({ theme }) => theme.colorDividerDefault};
  border-radius: 10px;

  .left {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  .icon-wrap {
    width: 36px;
    height: 36px;
    background: ${({ theme }) => theme.colorDividerDefault};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colorOrange};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .count {
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colorOrange};
  }
`

export const CostBarRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0.6rem 0;

  .top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .amt {
    display: inline-flex;
    gap: 8px;
    color: ${({ theme }) => theme.textColor};
    font-weight: 700;
    font-size: 14px;
  }

  .pct {
    color: ${({ theme }) => theme.textFaded};
    font-weight: 500;
    font-size: 12px;
  }
`

export const LinearTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.componentBackground};
  overflow: hidden;
`

export const LinearFill = styled.div<{ width: number; color: string }>`
  width: ${(p) => Math.min(Math.max(p.width, 0), 100)}%;
  height: 100%;
  background: ${(p) => p.color};
  border-radius: 3px;
`

export const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colorDividerDefault};
  margin: 0.85rem 0 0.75rem;
`

export const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  strong {
    color: ${({ theme }) => theme.colorOrange};
    font-size: 16px;
  }
`

export const UtilRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0.65rem 0;

  .label-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .value {
    font-size: 13px;
    font-weight: 700;

    .target {
      color: ${({ theme }) => theme.textFaded};
      font-weight: 400;
      margin-left: 4px;
    }
  }
`

export const UtilBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.componentBackground};
  border-radius: 3px;
  position: relative;
  overflow: hidden;
`

export const UtilFill = styled.div<{ width: number; color: string }>`
  width: ${(p) => Math.min(Math.max(p.width, 0), 100)}%;
  height: 100%;
  background: ${(p) => p.color};
  border-radius: 3px;
`

export const UtilMark = styled.div<{ position: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  left: ${(p) => Math.min(Math.max(p.position, 0), 100)}%;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.75;
`

export const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const AlertItem = styled.div<{ severity: 'critical' | 'high' | 'medium' | 'low' }>`
  position: relative;
  padding: 0.85rem 1rem 0.85rem 1.1rem;
  border-radius: 8px;
  background: ${({ theme }) => theme.colorSurfaceBase};
  border: 1px solid ${({ theme }) => theme.colorDividerDefault};
  border-left: 3px solid
    ${({ severity }) =>
      severity === 'critical'
        ? fleetColors.danger
        : severity === 'high'
          ? fleetColors.warning
          : severity === 'medium'
            ? fleetColors.yellow
            : fleetColors.info};

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }
`

export const SeverityPill = styled.span<{ severity: 'critical' | 'high' | 'medium' | 'low' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: lowercase;
  border: 1px solid
    ${({ severity }) =>
      severity === 'critical'
        ? fleetColors.danger
        : severity === 'high'
          ? fleetColors.warning
          : severity === 'medium'
            ? fleetColors.yellow
            : fleetColors.info};
  color: ${({ severity }) =>
    severity === 'critical'
      ? fleetColors.danger
      : severity === 'high'
        ? fleetColors.warning
        : severity === 'medium'
          ? fleetColors.yellow
          : fleetColors.info};
`

export const AlertCount = styled.span`
  background: transparent;
  border: 1px solid ${fleetColors.danger};
  color: ${fleetColors.danger};
  border-radius: 999px;
  padding: 1px 9px;
  font-size: 11px;
  font-weight: 700;
`

export const ComingSoonWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;

  .cs-blur {
    filter: blur(4px);
    pointer-events: none;
    user-select: none;
  }

  .cs-label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textColor};
    background: rgba(0, 0, 0, 0.18);
  }
`
